import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Calendar, List, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Download, Filter, TrendingUp, FileSpreadsheet,
  Users, Truck, FileText, Sparkles, AlertCircle, RefreshCw
} from "lucide-react";

const DAYS_ES = ["L", "M", "X", "J", "V", "S", "D"];
const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const PERIODS = [
  { id: "today",   label: "Hoy" },
  { id: "week",    label: "Esta semana" },
  { id: "month",   label: "Este mes" },
  { id: "3months", label: "Últimos 3 meses" },
  { id: "year",    label: "Este año" },
  { id: "custom",  label: "Personalizado" },
];

function getPeriodDates(period, customFrom, customTo) {
  const today = new Date();
  const pad = n => String(n).padStart(2, "0");
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const todayStr = fmt(today);

  switch (period) {
    case "today":
      return { from: todayStr, to: todayStr };
    case "week": {
      const dow = (today.getDay() + 6) % 7; // Mon=0
      const mon = new Date(today); mon.setDate(today.getDate() - dow);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return { from: fmt(mon), to: fmt(sun) };
    }
    case "month": {
      const from = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`;
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      return { from, to: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(lastDay)}` };
    }
    case "3months": {
      const from3 = new Date(today); from3.setMonth(today.getMonth() - 2); from3.setDate(1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      return { from: fmt(from3), to: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(lastDay)}` };
    }
    case "year":
      return { from: `${today.getFullYear()}-01-01`, to: `${today.getFullYear()}-12-31` };
    case "custom":
      return { from: customFrom, to: customTo };
    default:
      return { from: todayStr, to: todayStr };
  }
}

function sourceTag(source) {
  if (source === "import")
    return <span className="flex items-center gap-1 text-[10px] font-semibold text-purple-600 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded-full whitespace-nowrap"><Sparkles size={8} />IA</span>;
  return <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">Manual</span>;
}

function reliabilityBadge(r) {
  if (!r) return null;
  if (r === "high") return <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">Alta</span>;
  if (r === "medium") return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">Media</span>;
  return <span className="text-[10px] font-bold text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-full">Revisar</span>;
}

// ── Calendar View ─────────────────────────────────────────────────────────────
function CalendarView({ reports, calYear, calMonth, setCalYear, setCalMonth, onDayClick }) {
  const reportsByDate = useMemo(() => {
    const m = {};
    for (const r of reports) {
      if (!m[r.date]) m[r.date] = [];
      m[r.date].push(r);
    }
    return m;
  }, [reports]);

  const firstDay = new Date(calYear, calMonth, 1);
  const lastDay  = new Date(calYear, calMonth + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7;
  const today = new Date().toISOString().slice(0, 10);
  const pad = n => String(n).padStart(2, "0");

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(d);

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
  };

  return (
    <div className="card-white p-5">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"><ChevronLeft size={16} /></button>
        <span className="font-bold text-slate-800">{MONTHS_ES[calMonth]} {calYear}</span>
        <button onClick={nextMonth} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"><ChevronRight size={16} /></button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-2">
        {DAYS_ES.map(d => (
          <div key={d} className={`text-center text-[11px] font-bold py-1.5 ${d === "S" || d === "D" ? "text-slate-300" : "text-slate-400"}`}>{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const dateStr = `${calYear}-${pad(calMonth + 1)}-${pad(day)}`;
          const dayReports = reportsByDate[dateStr] || [];
          const isToday = dateStr === today;
          const hasReport = dayReports.length > 0;
          const totalEntries = dayReports.reduce((s, r) => s + (r.total_staff || 0), 0);
          const avgCompletion = dayReports.length > 0
            ? Math.round(dayReports.reduce((s, r) => s + (r.completion_rate || 0), 0) / dayReports.length)
            : null;

          return (
            <button
              key={dateStr}
              data-testid={`cal-day-${dateStr}`}
              onClick={() => hasReport && onDayClick(dateStr, dayReports)}
              className={`aspect-square rounded-xl flex flex-col items-center justify-center p-1 transition-all text-center min-h-[52px]
                ${hasReport ? "cursor-pointer hover:scale-105 hover:shadow-md" : "cursor-default"}
                ${isToday ? "ring-2 ring-[#007934] ring-offset-1" : ""}
                ${hasReport
                  ? avgCompletion === 100 ? "bg-[#E6F4EA] border border-[#007934]/20"
                    : avgCompletion >= 80 ? "bg-amber-50 border border-amber-200"
                    : "bg-red-50 border border-red-100"
                  : "hover:bg-slate-50"
                }
              `}
            >
              <span className={`text-sm font-bold leading-none ${
                isToday ? "text-[#007934]" : hasReport ? "text-slate-700" : "text-slate-400"
              }`}>{day}</span>
              {hasReport && (
                <>
                  <span className="text-[9px] font-bold mt-0.5 text-slate-500 leading-none">{totalEntries} pers.</span>
                  <div className={`w-1.5 h-1.5 rounded-full mt-0.5 ${
                    avgCompletion === 100 ? "bg-[#007934]" : avgCompletion >= 80 ? "bg-amber-500" : "bg-red-400"
                  }`} />
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100 text-[10px] text-slate-400 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#E6F4EA] border border-[#007934]/20" />100% completado</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-50 border border-amber-200" />80-99%</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-50 border border-red-100" />&lt;80%</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full ring-2 ring-[#007934]" />Hoy</span>
      </div>
    </div>
  );
}

// ── Day Detail Drawer ─────────────────────────────────────────────────────────
function DayDrawer({ date, reports, contracts, onClose, onNavigate }) {
  if (!date) return null;
  const [d, m, y] = [date.slice(8), parseInt(date.slice(5, 7)) - 1, date.slice(0, 4)];
  const dateLabel = `${parseInt(d)} ${MONTHS_ES[m]} ${y}`;
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">{dateLabel}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><XCircle size={18} /></button>
        </div>
        <div className="space-y-3">
          {reports.map((r, i) => {
            const contract = contracts.find(c => c.id === r.contract_id);
            return (
              <button key={i} onClick={() => onNavigate(r)} className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-[#007934]/30 hover:bg-[#E6F4EA] transition-all text-left">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: (contract?.color || "#007934") + "20" }}>
                  <ClipboardList size={16} style={{ color: contract?.color || "#007934" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm">{contract?.name || r.contract_id}</p>
                  <p className="text-xs text-slate-400">{r.total_staff} pers. · {r.total_vehicles} veh. · {r.completion_rate}% cumpl.</p>
                </div>
                <ChevronRight size={14} className="text-slate-300" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function WorkReportsList() {
  const navigate = useNavigate();
  const { authAxios } = useAuth();

  const today = new Date();
  const [contracts, setContracts] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const [period, setPeriod] = useState("year");
  const [contractFilter, setContractFilter] = useState("all");
  const [customFrom, setCustomFrom] = useState(today.toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(today.toISOString().slice(0, 10));
  const [viewMode, setViewMode] = useState("list"); // "list" | "calendar"

  // Calendar state
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  // Day drawer
  const [drawerDate, setDrawerDate] = useState(null);
  const [drawerReports, setDrawerReports] = useState([]);

  // Monthly export modal
  const [showMonthlyExport, setShowMonthlyExport] = useState(false);
  const [monthlyContractId, setMonthlyContractId] = useState("");
  const [monthlyMonth, setMonthlyMonth] = useState(today.toISOString().slice(0, 7));
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  const exportMonthly = async () => {
    if (!monthlyContractId || !monthlyMonth) {
      toast.error("Selecciona contrato y mes");
      return;
    }
    setMonthlyLoading(true);
    try {
      const res = await authAxios.get("/work-reports/export-monthly-by-service", {
        params: { contract_id: monthlyContractId, month: monthlyMonth },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cname = contracts.find((c) => c.id === monthlyContractId)?.name || "contrato";
      a.download = `Parte_Mensual_${cname.replace(/\s+/g, "_")}_${monthlyMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Excel mensual generado");
      setShowMonthlyExport(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el Excel");
    } finally {
      setMonthlyLoading(false);
    }
  };

  useEffect(() => {
    authAxios.get("/contracts").then(r => setContracts(r.data));
  }, []);

  const { from, to } = getPeriodDates(period, customFrom, customTo);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: from, date_to: to, limit: 500 });
      if (contractFilter !== "all") params.set("contract_id", contractFilter);
      const r = await authAxios.get(`/work-reports?${params}`);
      setReports(r.data);
    } catch (_) {}
    setLoading(false);
  }, [from, to, contractFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // When switching to calendar view, set the calendar month to the period's start
  useEffect(() => {
    if (viewMode === "calendar" && from) {
      setCalYear(parseInt(from.slice(0, 4)));
      setCalMonth(parseInt(from.slice(5, 7)) - 1);
    }
  }, [viewMode, from]);

  // Summary stats
  const stats = useMemo(() => {
    const totalStaff = reports.reduce((s, r) => s + (r.total_staff || 0), 0);
    const totalVehicles = reports.reduce((s, r) => s + (r.total_vehicles || 0), 0);
    const avgCompletion = reports.length
      ? Math.round(reports.reduce((s, r) => s + (r.completion_rate || 0), 0) / reports.length)
      : 0;
    const imported = reports.filter(r => r.source === "import").length;
    return { count: reports.length, totalStaff, totalVehicles, avgCompletion, imported };
  }, [reports]);

  const contractName = id => contracts.find(c => c.id === id)?.name || id;
  const contractColor = id => contracts.find(c => c.id === id)?.color || "#007934";

  const onDayClick = (date, reps) => {
    setDrawerDate(date);
    setDrawerReports(reps);
  };

  return (
    <div className="p-6 space-y-5">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-3 flex-wrap animate-fade-in-up">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <ClipboardList size={20} className="text-[#007934]" />
            <h1 className="text-2xl font-black text-slate-900">Histórico de Partes</h1>
          </div>
          <p className="text-slate-400 text-sm">{stats.count} partes en el período seleccionado</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="refresh-reports-btn"
            onClick={fetchReports}
            className="p-2 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-slate-500"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            data-testid="export-monthly-btn"
            onClick={() => setShowMonthlyExport(true)}
            className="flex items-center gap-2 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-4 py-2.5 rounded-xl font-semibold text-xs transition-all"
            title="Exportar Excel agregado mensual por servicio"
          >
            <FileSpreadsheet size={14} /> Excel mensual
          </button>
          <button
            data-testid="new-report-btn"
            onClick={() => navigate("/work-reports/new")}
            className="flex items-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md transition-all active:scale-95"
          >
            <Plus size={15} /> Nuevo Parte
          </button>
        </div>
      </div>

      {/* ── Filters row ── */}
      <div className="flex items-center gap-3 flex-wrap animate-fade-in-up">
        {/* Period tabs */}
        <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-1 flex-wrap">
          {PERIODS.map(p => (
            <button
              key={p.id}
              data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap
                ${period === p.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom range */}
        {period === "custom" && (
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#007934]/20" />
            <span className="text-slate-400">—</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#007934]/20" />
          </div>
        )}

        {/* Contract filter */}
        <select
          data-testid="contract-filter-select"
          value={contractFilter}
          onChange={e => setContractFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20 font-semibold"
        >
          <option value="all">Todos los contratos</option>
          {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        {/* View mode toggle */}
        <div className="ml-auto flex items-center bg-slate-100 rounded-xl p-1 gap-1">
          <button
            data-testid="view-list-btn"
            onClick={() => setViewMode("list")}
            className={`p-1.5 rounded-lg transition-all ${viewMode === "list" ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"}`}
          >
            <List size={14} />
          </button>
          <button
            data-testid="view-calendar-btn"
            onClick={() => setViewMode("calendar")}
            className={`p-1.5 rounded-lg transition-all ${viewMode === "calendar" ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"}`}
          >
            <Calendar size={14} />
          </button>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 animate-fade-in-up">
        {[
          { icon: ClipboardList, label: "Partes",       value: stats.count,         color: "text-[#007934]", bg: "bg-[#E6F4EA]" },
          { icon: Users,         label: "Jornadas",     value: stats.totalStaff,    color: "text-blue-600",  bg: "bg-blue-50" },
          { icon: Truck,         label: "Vehículos",    value: stats.totalVehicles, color: "text-amber-600", bg: "bg-amber-50" },
          { icon: TrendingUp,    label: "% Cumpl. medio",value: `${stats.avgCompletion}%`, color: stats.avgCompletion >= 90 ? "text-emerald-600" : "text-red-500", bg: stats.avgCompletion >= 90 ? "bg-emerald-50" : "bg-red-50" },
          { icon: Sparkles,      label: "Importados IA",value: stats.imported,      color: "text-purple-600", bg: "bg-purple-50" },
        ].map(({ icon: Icon, label, value, color, bg }) => (
          <div key={label} className="card-white p-4 flex items-center gap-3">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
              <Icon size={15} className={color} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-400 font-semibold truncate">{label}</p>
              <p className={`text-lg font-black ${color}`}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Calendar view ── */}
      {viewMode === "calendar" && (
        <div className="animate-fade-in-up">
          <CalendarView
            reports={reports}
            calYear={calYear}
            calMonth={calMonth}
            setCalYear={setCalYear}
            setCalMonth={setCalMonth}
            onDayClick={onDayClick}
          />
        </div>
      )}

      {/* ── List view ── */}
      {viewMode === "list" && (
        <div className="card-white overflow-hidden animate-fade-in-up">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-[#007934] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <ClipboardList size={36} className="mx-auto mb-3 opacity-30" />
              <p className="font-semibold text-slate-500">No hay partes en este período</p>
              <p className="text-sm mt-1 mb-4">Cambia el filtro de periodo o crea un nuevo parte</p>
              <button
                onClick={() => navigate("/work-reports/new")}
                className="inline-flex items-center gap-2 bg-[#007934] text-white px-4 py-2 rounded-xl text-sm font-semibold"
              >
                <Plus size={14} /> Crear Parte
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Contrato</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wide">Jornadas</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wide">Vehículos</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wide">% Cumpl.</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Origen</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Fiabilidad</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Estado</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reports.map((r, i) => {
                    const pct = r.completion_rate || 0;
                    const pctColor = pct === 100 ? "text-emerald-600" : pct >= 80 ? "text-amber-600" : "text-red-500";
                    return (
                      <tr
                        key={r.id}
                        data-testid={`report-row-${i}`}
                        className="hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/work-reports/${r.id}`)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <p className="font-bold text-slate-800">{new Date(r.date + "T12:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}</p>
                          <p className="text-[11px] text-slate-400">{r.day_of_week}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: contractColor(r.contract_id) }} />
                            <span className="font-medium text-slate-700 text-xs">{contractName(r.contract_id)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold text-slate-800">{r.total_staff ?? "—"}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-slate-600">{r.total_vehicles ?? "—"}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-bold text-sm ${pctColor}`}>{pct}%</span>
                        </td>
                        <td className="px-4 py-3">{sourceTag(r.source)}</td>
                        <td className="px-4 py-3">{reliabilityBadge(r.reliability)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            r.status === "final" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                          }`}>{r.status === "final" ? "Final" : "Borrador"}</span>
                        </td>
                        <td className="px-4 py-3 flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            data-testid={`export-pdf-btn-${i}`}
                            title="Exportar PDF"
                            onClick={async () => {
                              try {
                                const res = await authAxios.get(`/work-reports/${r.id}/export-pdf`, { responseType: "blob" });
                                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `Parte_${r.id.slice(0,6)}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              } catch { toast.error("Error generando PDF"); }
                            }}
                            className="p-1.5 text-slate-300 hover:text-[#007934] hover:bg-[#E6F4EA] rounded-lg transition-all"
                          >
                            <Download size={13} />
                          </button>
                          <button
                            data-testid={`export-xlsx-btn-${i}`}
                            title="Exportar Excel del día"
                            onClick={async () => {
                              try {
                                const res = await authAxios.get(`/work-reports/${r.id}/export-excel`, { responseType: "blob" });
                                const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `Parte_${r.id.slice(0,6)}.xlsx`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              } catch { toast.error("Error generando Excel"); }
                            }}
                            className="p-1.5 text-slate-300 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all"
                          >
                            <FileSpreadsheet size={13} />
                          </button>
                          <ChevronRight size={14} className="text-slate-300" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Day drawer */}
      <DayDrawer
        date={drawerDate}
        reports={drawerReports}
        contracts={contracts}
        onClose={() => setDrawerDate(null)}
        onNavigate={r => { setDrawerDate(null); navigate(`/work-reports/${r.id}`); }}
      />

      {/* Monthly export modal */}
      {showMonthlyExport && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" data-testid="monthly-export-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-scale-in">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <FileSpreadsheet size={18} className="text-emerald-600" /> Excel mensual por servicio
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Genera un Excel agregado: filas = servicios del catálogo, columnas = días del mes,
                  celdas = personas y vehículos asignados.
                </p>
              </div>
              <button onClick={() => setShowMonthlyExport(false)} className="text-slate-400 hover:text-slate-600" data-testid="monthly-export-close">
                <XCircle size={18} />
              </button>
            </div>

            <div className="space-y-3 mt-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Contrato</label>
                <select
                  value={monthlyContractId}
                  onChange={(e) => setMonthlyContractId(e.target.value)}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
                  data-testid="monthly-export-contract"
                >
                  <option value="">— Selecciona contrato —</option>
                  {contracts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Mes</label>
                <input
                  type="month"
                  value={monthlyMonth}
                  onChange={(e) => setMonthlyMonth(e.target.value)}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
                  data-testid="monthly-export-month"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowMonthlyExport(false)}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={exportMonthly}
                disabled={monthlyLoading || !monthlyContractId}
                className="px-5 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="monthly-export-confirm"
              >
                {monthlyLoading ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
                Generar Excel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
